import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { TAG_TONES, TAG_TONE_LIST, tagPillClass, toneFor } from "./clientTags";
import {
  createClientTag,
  deleteClientTag,
  fetchClientTags,
  updateClientTag,
} from "../services/clientService";

/**
 * The labels a business puts on its own clients — the segments BIZ-15 filters
 * by.
 *
 * Managed here rather than on a screen of their own: a tag exists to be put on
 * somebody, so it belongs next to the list it filters. Deleting one takes it
 * off every client wearing it, which the confirmation says out loud.
 */
export default function ClientTagsModal({ open, onClose, onChanged }) {
  const { t } = useTranslation();

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  // A tag now carries an operational tone, not a decorative colour. The hex the
  // API stores is derived from it on save.
  const [tone, setTone] = useState(TAG_TONES.neutral);
  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      setTags(await fetchClientTags());
    } catch (err) {
      setError(apiErrorMessage(err, t("clients.tags.loadFailed")));
      setTags([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    setName("");
    setTone(TAG_TONES.neutral);
    setEditing(null);
    setConfirming(null);
    setFieldErrors({});
    load();
    // load is stable enough for this modal's lifetime; re-running it on every
    // render would refetch the list under the user's cursor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const startEdit = (tag) => {
    setEditing(tag);
    setName(tag.name);
    setTone(toneFor(tag));
    setFieldErrors({});
  };

  const cancelEdit = () => {
    setEditing(null);
    setName("");
    setTone(TAG_TONES.neutral);
    setFieldErrors({});
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setFieldErrors({});

    try {
      if (editing) {
        await updateClientTag(editing.id, { name: name.trim(), colour: tone.hex });
      } else {
        await createClientTag({ name: name.trim(), colour: tone.hex });
      }

      cancelEdit();
      await load();
      onChanged?.();
    } catch (err) {
      setFieldErrors(apiFieldErrors(err));
      setError(apiErrorMessage(err, t("clients.tags.saveFailed")));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (tag) => {
    setBusy(true);
    setError("");
    try {
      await deleteClientTag(tag.id);
      setConfirming(null);
      await load();
      onChanged?.();
    } catch (err) {
      setError(apiErrorMessage(err, t("clients.tags.deleteFailed")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="card my-8 w-full max-w-lg p-5">
        <h2 className="mb-1 text-lg font-bold">{t("clients.tags.title")}</h2>
        <p className="mb-4 text-sm text-ink-2">{t("clients.tags.subtitle")}</p>

        <form onSubmit={submit} className="mb-4 space-y-3 rounded-lg border border-line p-3">
          <div>
            <label className="field-label" htmlFor="tag-name">
              {t("clients.tags.name")} <span className="text-danger-text">*</span>
            </label>
            <input
              id="tag-name"
              className="field-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {fieldErrors.name && <p className="field-error">{fieldErrors.name[0]}</p>}
          </div>

          <div>
            <p className="field-label" id="tag-tone-label">
              {t("clients.tags.tone")}
            </p>
            {/* Four operational tones, not a palette. Each one is named, so the
                tag means something to the next person who reads the list. */}
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby="tag-tone-label">
              {TAG_TONE_LIST.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  role="radio"
                  aria-checked={tone.key === option.key}
                  onClick={() => setTone(option)}
                  className={`min-h-touch gap-2 px-3 py-1 text-dense ${tagPillClass(
                    { colour: option.hex },
                    { selected: tone.key === option.key, interactive: true }
                  )}`}
                >
                  <span className={`h-2 w-2 rounded-full ${option.swatch}`} aria-hidden="true" />
                  {t(option.label)}
                </button>
              ))}
            </div>
            {fieldErrors.colour && <p className="field-error">{fieldErrors.colour[0]}</p>}
          </div>

          <div className="flex justify-end gap-2">
            {editing && (
              <button type="button" className="btn-secondary" onClick={cancelEdit} disabled={busy}>
                {t("common.cancel")}
              </button>
            )}
            <button type="submit" className="btn-primary" disabled={busy || !name.trim()}>
              {editing ? t("common.save") : t("clients.tags.add")}
            </button>
          </div>
        </form>

        {error && <p className="field-error mb-3">{error}</p>}

        {loading ? (
          <p className="py-6 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : tags.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-2">{t("clients.tags.none")}</p>
        ) : (
          <ul className="divide-y divide-line rounded-lg border border-line">
            {tags.map((tag) => (
              <li key={tag.id} className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="flex items-center gap-2">
                  {/* Outlined, always — a tag is never a filled colour field. */}
                  <span className={`${tagPillClass(tag)} px-3 py-1 text-caption`}>{tag.name}</span>
                  <span className="text-xs text-ink-2">
                    {t("clients.tags.wornBy", { count: tag.clients_count ?? 0 })}
                  </span>
                </span>

                {confirming === tag.id ? (
                  <span className="flex items-center gap-2 text-xs">
                    <span className="text-ink-2">{t("clients.tags.confirmDelete")}</span>
                    <button
                      type="button"
                      className="font-medium text-danger-text hover:underline"
                      disabled={busy}
                      onClick={() => remove(tag)}
                    >
                      {t("common.delete")}
                    </button>
                    <button
                      type="button"
                      className="text-ink-2 hover:underline"
                      onClick={() => setConfirming(null)}
                    >
                      {t("common.cancel")}
                    </button>
                  </span>
                ) : (
                  <span className="flex gap-3 text-sm">
                    <button
                      type="button"
                      className="font-medium text-accent-text hover:underline"
                      onClick={() => startEdit(tag)}
                    >
                      {t("common.edit")}
                    </button>
                    <button
                      type="button"
                      className="font-medium text-danger-text hover:underline"
                      onClick={() => setConfirming(tag.id)}
                    >
                      {t("common.delete")}
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 flex justify-end">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}
